import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PhoneIcon from "@mui/icons-material/Phone";
import SentimentDissatisfiedIcon from "@mui/icons-material/SentimentDissatisfied";
import { Box, Divider, Grid, Stack, Typography, useTheme } from "@mui/material";
import { AiStarIcon } from "components/icons/AiStarIcon";
import { CheckCircleIcon } from "components/icons/CheckCircleIcon";
import type { ElementType } from "react";

export interface DetailRow {
  label: string;
  value: string;
}

export interface TriageRouting {
  board?: string;
  status?: string;
  tagCount?: number;
}

export interface AITriageNoteProps {
  enrichedSubject?: string;
  enrichedDescription?: string;
  details?: DetailRow[];
  checklist?: string[];
  agentActions?: string[];
  routing?: TriageRouting;
  contactInfo?: string | null;
  startedFrom?: string | null;
  sentiment?: string | null;
  interruptedNote?: string | null;
}

const DEFAULT_DETAILS: DetailRow[] = [
  { label: "User", value: "Josh" },
  { label: "Site", value: "HQ" },
  { label: "Device", value: "Dell XPS 15 laptop" },
  {
    label: "Impact",
    value: "User unable to access Outlook; other applications working normally",
  },
  { label: "Urgency", value: "Medium (user impacted, not system-wide)" },
  { label: "Priority", value: "P3 – Individual user issue" },
  {
    label: "Screenshot",
    value: "Yes – user provided Outlook “Not Responding” screenshot",
  },
  { label: "Troubleshooting done so far", value: "Yes" },
];

const DEFAULT_CHECKLIST: string[] = [
  "Identified issue isolated to Outlook application",
  "AI guided user through basic checks",
  "Confirmed system and browser working fine",
];

// ─── Sub-components ─────────────────────────────────────────────────

interface IconRowProps {
  icon: ElementType;
  iconColor?: string;
  text: string;
  textColor?: string;
}

const IconRow = ({ icon: Icon, iconColor, text, textColor }: IconRowProps) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
    <Icon sx={{ color: iconColor, fontSize: "16px", flexShrink: 0 }} />
    <Typography variant="body-small" sx={{ color: textColor }}>
      {text}
    </Typography>
  </Box>
);

const DetailsList = ({ details }: { details: DetailRow[] }) => {
  const theme = useTheme();
  if (details.length === 0) return null;
  return (
    <Stack sx={{ gap: "8px" }}>
      {details.map((item) => (
        <Box key={item.label} sx={{ display: "flex", gap: "4px" }}>
          <Typography
            variant="body-small"
            sx={{ color: theme.palette.lightBg.high, flexShrink: 0 }}
          >
            {item.label} :
          </Typography>
          <Typography
            variant="body-small"
            sx={{ color: theme.palette.lightBg.low }}
          >
            {item.value}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
};

const TriageResults = ({
  checklist,
  contactInfo,
  startedFrom,
  sentiment,
}: {
  checklist: string[];
  contactInfo?: string | null;
  startedFrom?: string | null;
  sentiment?: string | null;
}) => {
  const theme = useTheme();
  const violet = theme.palette.primitive.system.violet[800];
  const hasChecklist = checklist.length > 0;
  const hasSideColumn = Boolean(contactInfo || startedFrom || sentiment);
  if (!(hasChecklist || hasSideColumn)) return null;

  return (
    <>
      <Divider sx={{ borderColor: theme.palette.neutral["015"] }} />
      <Grid container spacing={3}>
        {hasChecklist && (
          <Grid item xs={12} md={hasSideColumn ? 6 : 12}>
            <Stack sx={{ gap: "12px" }}>
              {checklist.map((text) => (
                <IconRow
                  key={text}
                  icon={CheckCircleIcon}
                  iconColor={violet}
                  text={text}
                  textColor={theme.palette.lightBg.high}
                />
              ))}
            </Stack>
          </Grid>
        )}
        {hasSideColumn && (
          <Grid item xs={12} md={hasChecklist ? 6 : 12}>
            <Stack sx={{ gap: "12px" }}>
              {contactInfo ? (
                <IconRow
                  icon={PhoneIcon}
                  iconColor={violet}
                  text={contactInfo}
                  textColor={theme.palette.lightBg.high}
                />
              ) : null}
              {startedFrom ? (
                <IconRow
                  icon={AccessTimeIcon}
                  iconColor={violet}
                  text={startedFrom}
                  textColor={theme.palette.lightBg.high}
                />
              ) : null}
              {sentiment ? (
                <IconRow
                  icon={SentimentDissatisfiedIcon}
                  iconColor={theme.palette.primitive.system.yellow[800]}
                  text={sentiment}
                  textColor={theme.palette.lightBg.high}
                />
              ) : null}
            </Stack>
          </Grid>
        )}
      </Grid>
    </>
  );
};

const InterruptedNote = ({ note }: { note?: string | null }) => {
  const theme = useTheme();
  if (!note) return null;
  return (
    <>
      <Divider sx={{ borderColor: theme.palette.neutral["015"] }} />
      <Typography
        variant="body-small"
        sx={{
          color: theme.palette.primitive.system.orange[800],
          fontWeight: 600,
        }}
      >
        {note}
      </Typography>
    </>
  );
};

// ─── Card ───────────────────────────────────────────────────────────

export const AITriageNote = ({
  enrichedSubject,
  enrichedDescription,
  details = DEFAULT_DETAILS,
  checklist = DEFAULT_CHECKLIST,
  agentActions = [],
  routing,
  contactInfo = "Contact info: +1-584-395-8539",
  startedFrom = "Issue started from: This morning",
  sentiment = "Sentiment: Frustrated but cooperative",
  interruptedNote = "Note: AI triaging interrupted... John Doe took control",
}: AITriageNoteProps) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        background:
          "linear-gradient(104.09deg, rgba(227, 231, 255, 0.3) 0%, rgba(255, 255, 255, 0) 44.08%), linear-gradient(0deg, #FFFFFF, #FFFFFF)",
        border: `1px solid ${theme.palette.neutral["005"]}`,
        borderRadius: "8px",
        padding: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        width: "100%",
        boxSizing: "border-box",
        boxShadow: "0px 5px 5px 0px #00000008, 0px 20px 8px 0px #00000003",
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <AiStarIcon
          className="ai-star-spin"
          sx={{
            color: theme.palette.primitive.system.violet[800],
            fontSize: "8px",
          }}
        />
        <Typography
          variant="subtitle1"
          sx={{ color: theme.palette.lightBg.high }}
        >
          AI triage note
        </Typography>
      </Box>

      <DetailsList details={details} />
      <TriageResults
        checklist={checklist}
        contactInfo={contactInfo}
        startedFrom={startedFrom}
        sentiment={sentiment}
      />

      <InterruptedNote note={interruptedNote} />
    </Box>
  );
};
